<template>
  <v-main class="student-report">
    <div class="student-report__inner">
      <header class="student-report__title">
        <div>
          <h1>Табель успішності</h1>
          <p>{{ reportSubtitle }}</p>
        </div>
        <button
          v-if="!isGuest"
          type="button"
          class="student-report__download"
          :disabled="!isReady || downloading"
          @click="downloadPdf"
        >
          <v-icon size="20">
            {{ downloading ? 'mdi-loading' : 'mdi-download' }}
          </v-icon>
          {{ downloading ? 'Завантаження...' : 'Завантажити PDF' }}
        </button>
        <span v-else class="student-report__demo-badge">Демонстраційний приклад</span>
      </header>

      <v-progress-linear
        v-if="loading && !isGuest"
        indeterminate
        class="student-report__progress theme-loader"
      />

      <section v-if="error && !loading" class="student-report__state">
        <span
          ><v-icon color="#df3f35" size="36"
            >mdi-alert-circle-outline</v-icon
          ></span
        >
        <h2>Не вдалося завантажити табель</h2>
        <p>Перевір з’єднання та спробуй ще раз.</p>
        <button type="button" @click="loadReport">Повторити</button>
      </section>

      <template v-else-if="!loading && report">
        <section class="student-report__info">
          <div class="student-report__student">
            <span class="student-report__document-icon">
              <v-icon size="27"
                >mdi-file-document-outline</v-icon
              >
            </span>
            <div>
              <strong>{{ report.student.full_name || 'Учень' }}</strong>
              <p>{{ studentDetails }}</p>
            </div>
          </div>
          <div class="student-report__meta">
            <span>{{ academicYearLabel }}</span>
            <span>{{ updatedAtLabel }}</span>
          </div>
        </section>

        <section v-if="isReady" class="student-report__preview">
          <div class="student-report__summary">
            <article>
              <span
                ><v-icon size="23"
                  >mdi-book-check-outline</v-icon
                ></span
              >
              <div>
                <strong
                  >{{ report.summary.subjects_with_grades }} з
                  {{ report.summary.subjects_count }}</strong
                >
                <small>предметів з оцінками</small>
              </div>
            </article>
            <article>
              <span
                ><v-icon size="23">mdi-chart-line</v-icon></span
              >
              <div>
                <strong>{{ report.summary.average_grade || '—' }}</strong>
                <small>середній бал</small>
              </div>
            </article>
          </div>

          <div class="student-report__table-wrap">
            <table>
              <colgroup>
                <col class="student-report__col-number" />
                <col class="student-report__col-subject" />
                <col class="student-report__col-grade" />
                <col class="student-report__col-grade" />
                <col class="student-report__col-grade" />
              </colgroup>
              <thead>
                <tr>
                  <th>№</th>
                  <th>Предмет</th>
                  <th>I семестр</th>
                  <th>II семестр</th>
                  <th>Річна</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in sortedRows"
                  :key="`${row.number}-${row.subject}`"
                >
                  <td>{{ index + 1 }}</td>
                  <td>{{ row.subject }}</td>
                  <td>
                    <span class="student-report__grade">{{
                      formatGrade(row.first_semester)
                    }}</span>
                  </td>
                  <td>
                    <span class="student-report__grade">{{
                      formatGrade(row.second_semester)
                    }}</span>
                  </td>
                  <td>
                    <span
                      class="student-report__grade student-report__grade--year"
                      >{{ formatGrade(row.year) }}</span
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="isGuest" class="student-report__demo-cta">
            <span>Хочете бачити реальні результати навчання в одному кабінеті?</span>
            <button type="button" @click="openDemoRequest">
              Залишити контакти
              <v-icon size="18">mdi-arrow-right</v-icon>
            </button>
          </div>
        </section>

        <section v-else class="student-report__state">
          <span
            ><v-icon class="student-report__state-icon" size="38"
              >mdi-information-outline</v-icon
            ></span
          >
          <h2>{{ report.title || 'Табель ще не готовий' }}</h2>
          <p>{{ report.description || 'Дані для табеля поки недоступні.' }}</p>
        </section>
      </template>
    </div>
  </v-main>
</template>

<script>
import popupEnum from '@/enums/popupEnum';

const DEMO_REPORT = Object.freeze({
  state: 'ready',
  description: 'Підсумкові оцінки за навчальний рік',
  student: { full_name: 'Учень RedFox', grade: 11 },
  academic_year: { year: 'демонстраційний' },
  updated_at: null,
  summary: {
    subjects_with_grades: 6,
    subjects_count: 6,
    average_grade: '9,5',
  },
  rows: [
    { number: 1, subject: 'Англійська мова', first_semester: 9, second_semester: 10, year: 10 },
    { number: 2, subject: 'Геометрія', first_semester: 8, second_semester: 9, year: 9 },
    { number: 3, subject: 'Інформатика', first_semester: 11, second_semester: 11, year: 11 },
    { number: 4, subject: 'Мистецтво', first_semester: 10, second_semester: 10, year: 10 },
    { number: 5, subject: 'Українська мова', first_semester: 9, second_semester: 9, year: 9 },
    { number: 6, subject: 'Хімія', first_semester: 8, second_semester: 9, year: 9 },
  ],
});

export default {
  name: 'ReportCardPage',
  data: () => ({
    loading: false,
    downloading: false,
    error: null,
  }),
  computed: {
    isGuest() {
      return this.$store.getters['UserStore/isGuest']();
    },
    report() {
      if (this.isGuest) return DEMO_REPORT;
      return this.$store.getters['LibraryStore/getStudentReportCard'];
    },
    sortedRows() {
      const rows = this.report && this.report.rows ? this.report.rows : [];
      const collator = new Intl.Collator('uk', { sensitivity: 'base' });
      return [...rows].sort((a, b) =>
        collator.compare(a.subject || '', b.subject || '')
      );
    },
    isReady() {
      return Boolean(
        this.report && this.report.state === 'ready' && this.report.rows.length
      );
    },
    reportSubtitle() {
      if (this.isGuest)
        return 'Приклад того, як учень і батьки бачать результати навчання';
      return this.report && this.report.description
        ? this.report.description
        : 'Підсумкові оцінки за поточний навчальний рік';
    },
    studentDetails() {
      if (!this.report || !this.report.student) return '';
      const student = this.report.student;
      const classNumber = student.grade || student.group;
      return classNumber ? `${classNumber} клас` : 'Клас не вказано';
    },
    academicYearLabel() {
      if (this.isGuest) return 'Демонстраційний навчальний рік';
      if (!this.report || !this.report.academic_year)
        return 'Навчальний рік не вказано';
      const year = this.report.academic_year;
      return year.year
        ? `Навчальний рік ${year.year}`
        : 'Навчальний рік не вказано';
    },
    updatedAtLabel() {
      if (this.isGuest) return 'Дані не належать реальному учню';
      if (!this.report || !this.report.updated_at)
        return 'Оцінок для оновлення ще немає';
      return `Оновлено: ${this.formatDate(this.report.updated_at)}`;
    },
  },
  mounted() {
    if (!this.isGuest) this.loadReport();
  },
  methods: {
    openDemoRequest() {
      this.$store.dispatch('PopupStore/openPopup', {
        popupName: popupEnum.DEMO_REQUEST_POPUP,
        popupContext: {
          courseName: 'Табель успішності',
          lessonName: 'Повний кабінет учня та батьків',
        },
      });
    },
    async loadReport() {
      this.loading = true;
      this.error = null;
      try {
        await this.$store.dispatch('LibraryStore/loadStudentReportCard');
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    formatGrade(value) {
      return value === null || value === undefined ? '—' : Math.round(value);
    },
    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('uk-UA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    },
    async downloadPdf() {
      if (!this.isReady || this.downloading) return;
      this.downloading = true;
      this.error = null;
      try {
        const response = await this.$store.dispatch(
          'LibraryStore/downloadStudentReportCard'
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.pdfFilename(response);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.error = error;
      } finally {
        this.downloading = false;
      }
    },
    pdfFilename(response) {
      const disposition = response.headers
        ? response.headers['content-disposition']
        : '';
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) return match[1];
      return 'report-card.pdf';
    },
  },
};
</script>

<style lang="less" scoped>
.student-report {
  background: var(--theme-app-background-accent);
  min-height: 100vh;
}
.student-report__inner {
  margin: 0 auto;
  max-width: 1480px;
  padding: 34px 28px 60px;
}
.student-report__title {
  align-items: flex-end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 22px;
}
.student-report__title h1 {
  color: #172033;
  font-size: 31px;
  margin: 0 0 7px;
}
.student-report__title p {
  color: #667085;
  margin: 0;
}
.student-report__download {
  align-items: center;
  background: var(--theme-header-background);
  border: 0;
  border-radius: 11px;
  color: var(--theme-header-text);
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  min-height: 46px;
  padding: 11px 17px;
}
.student-report__download:hover {
  filter: brightness(0.9);
}
.student-report__download::v-deep .v-icon {
  color: currentColor !important;
}
.student-report__download:disabled {
  cursor: default;
  opacity: 0.55;
}
.student-report__demo-badge {
  background: var(--theme-surface-decorative);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 999px;
  color: var(--theme-banner-text);
  font-size: 12px;
  font-weight: 850;
  padding: 8px 12px;
}
.student-report__progress {
  border-radius: 999px;
  margin-bottom: 18px;
}
.student-report__info {
  align-items: center;
  background: #fff;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 18px 20px;
}
.student-report__student {
  align-items: center;
  display: flex;
  gap: 15px;
}
.student-report__document-icon {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 13px;
  display: flex;
  flex: 0 0 auto;
  height: 50px;
  justify-content: center;
  width: 50px;
}
.student-report__document-icon::v-deep .v-icon {
  color: var(--theme-banner-text) !important;
}
.student-report__student strong {
  color: #172033;
  display: block;
  font-size: 18px;
}
.student-report__student p {
  color: #667085;
  margin: 4px 0 0;
}
.student-report__meta {
  align-items: flex-end;
  color: #667085;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: 4px;
}
.student-report__preview {
  background: #fff;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  overflow: hidden;
  padding: 20px;
}
.student-report__summary {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}
.student-report__summary article {
  align-items: center;
  background: #f8faf9;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 13px;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
}
.student-report__summary article > span {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 10px;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}
.student-report__summary article > span::v-deep .v-icon {
  color: var(--theme-header-background) !important;
}
.student-report__summary article:nth-child(2) > span {
  background: var(--theme-surface-decorative);
}
.student-report__summary strong,
.student-report__summary small {
  display: block;
}
.student-report__summary strong {
  color: var(--theme-header-background);
  font-size: 18px;
}
.student-report__summary small {
  color: #667085;
  margin-top: 2px;
}
.student-report__table-wrap {
  border: 1px solid var(--theme-border-decorative);
  border-radius: 12px;
  overflow-x: auto;
}
.student-report__demo-cta {
  align-items: center;
  border-top: 1px solid var(--theme-border-decorative);
  color: #667085;
  display: flex;
  font-size: 13px;
  gap: 18px;
  justify-content: flex-end;
  margin-top: 18px;
  padding-top: 16px;
}
.student-report__demo-cta button {
  align-items: center;
  background: transparent;
  border: 1px solid var(--theme-accent-decorative);
  border-radius: 9px;
  color: var(--theme-banner-text);
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 7px;
  min-height: 39px;
  padding: 8px 12px;
}
.student-report__demo-cta button:hover {
  background: var(--theme-surface-decorative);
}
.student-report__demo-cta button::v-deep .v-icon {
  color: currentColor !important;
}
.student-report table {
  border-collapse: collapse;
  min-width: 620px;
  table-layout: fixed;
  width: 100%;
}
.student-report__col-number {
  width: 64px;
}
.student-report__col-grade {
  width: 17%;
}
.student-report th,
.student-report td {
  border-bottom: 1px solid #e8ebee;
  padding: 13px 12px;
}
.student-report th {
  background: #f7f9fa;
  color: #596574;
  font-size: 12px;
  font-weight: 850;
  text-transform: uppercase;
}
.student-report th:last-child {
  background: var(--theme-surface-decorative);
  color: var(--theme-banner-text);
}
.student-report td {
  color: #465160;
  font-size: 14px;
}
.student-report tbody tr:last-child td {
  border-bottom: 0;
}
.student-report tbody tr:hover {
  background: var(--theme-surface-soft);
}
.student-report th:not(:nth-child(2)),
.student-report td:not(:nth-child(2)) {
  text-align: center;
}
.student-report td:nth-child(2) {
  color: #172033;
  font-weight: 700;
}
.student-report__grade {
  align-items: center;
  background: #f1f3f5;
  border-radius: 8px;
  display: inline-flex;
  font-weight: 850;
  justify-content: center;
  min-height: 32px;
  min-width: 36px;
  padding: 4px 7px;
}
.student-report__grade--year {
  background: var(--theme-surface-decorative);
  color: var(--theme-banner-text);
}
.student-report__state {
  align-items: center;
  background: #fff;
  border: 1px solid #e4e8eb;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  color: #667085;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 380px;
  padding: 36px;
  text-align: center;
}
.student-report__state > span {
  align-items: center;
  background: var(--theme-banner-background);
  border-radius: 15px;
  display: flex;
  height: 62px;
  justify-content: center;
  width: 62px;
}

.student-report__state-icon {
  color: var(--theme-header-background) !important;
}
.student-report__state h2 {
  color: #172033;
  margin: 18px 0 8px;
}
.student-report__state p {
  margin: 0 0 18px;
  max-width: 560px;
}
.student-report__state button {
  background: var(--theme-header-background);
  border: 0;
  border-radius: 10px;
  color: var(--theme-header-text);
  cursor: pointer;
  font-weight: 800;
  min-height: 44px;
  padding: 10px 18px;
}

@media (max-width: 760px) {
  .student-report__inner {
    padding: 24px 14px 40px;
  }
  .student-report__title {
    align-items: stretch;
    flex-direction: column;
  }
  .student-report__title h1 {
    font-size: 25px;
  }
  .student-report__download {
    width: 100%;
  }
  .student-report__demo-badge { align-self: flex-start; }
  .student-report__info {
    align-items: flex-start;
    flex-direction: column;
  }
  .student-report__meta {
    align-items: flex-start;
  }
  .student-report__summary {
    grid-template-columns: 1fr;
  }
  .student-report__preview {
    padding: 14px;
  }
  .student-report__demo-cta {
    align-items: stretch;
    flex-direction: column;
  }
  .student-report__demo-cta button { justify-content: center; }
}
</style>
